import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Styles from "./BusinessDescription.module.css";

import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";

import ActivityBar from "../../../components/Reusable/ActivityBar/ActivityBar";
import BusinessDescriptionForm from "../../../components/Reusable/Forms/BusinessDescriptionForm";
import SectionSlider from "../../../components/Reusable/SectionSlider/SectionSlider";

import { sections, sectionPaths } from "../../../constants/sectionData";
import { apiService } from "../../../api/apiService";
import useFunctionOptions from "../../../components/Reusable/utils/useFunctionOptions";
import CheckBDStatus from "../../../components/Reusable/Forms/FormSteps/CheckBDStatus";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import UploadExsitingBdPopup from "../../../components/Reusable/Popup/UploadExsitingBdPopup";

const BusinessDescription: React.FC = () => {
  const { instance } = useMsal();
  const management =
    localStorage.getItem("management") || "Default Location Name";
  const apiManagement =
    localStorage.getItem("apiManagement") || "Default Location Name";

  const [refreshKey, setRefreshKey] = useState(0);

  const [functionL1, setFunctionL1] = useState("");
  const [functionL2, setFunctionL2] = useState("");
  const [showSIPOC, setShowSIPOC] = useState(false);
  const [businessDescriptionData, setBusinessDescriptionData] =
    useState<any>(null);
  const [filteredBDData, setFilteredBDData] = useState<any>(null);
  const [isCheckingData, setIsCheckingData] = useState(false);
  const [apiStatus, setApiStatus] = useState<
    "initial" | "filtered" | "checked" | "error"
  >("initial");


  const [openUpload, setOpenUpload] = useState(false);

  // Check if management matches apiManagement
  const isManagementMatching = management === apiManagement;

  // Use the custom hook to fetch options for each level
  const { options: l1Options, loading: l1Loading } = useFunctionOptions(
    "l1",
    ""
  );
  const { options: l2Options, loading: l2Loading } = useFunctionOptions(
    "l2",
    functionL1
  );

  const checkBusinessDescriptions = async () => {
    if (functionL1 && functionL2) {
      try {
        setIsCheckingData(true);
        setApiStatus("initial");

        // First call the filter API
        try {
          const filterResponse = await apiService(
            "/management/business_description_by_functions",
            "GET",
            {
              function_l1: functionL1,
              function_l2: functionL2,
              management: management,
            },
            undefined,
            instance as PublicClientApplication
          );

          setFilteredBDData(filterResponse);
          setApiStatus("filtered");
        } catch (filterError) {
          // If 404 error, it means no BD found
          if (String(filterError as any).includes("404")) {
            setFilteredBDData(null);
            console.error(
              "No filtered business description found:",
              filteredBDData
            );
            setApiStatus("filtered");
          } else {
            console.error(
              "Error checking filtered business description:",
              filterError
            );
            setApiStatus("error");
          }
        }

        // Then call the check API if management matches
        if (isManagementMatching) {
          try {
            const checkResponse = await apiService(
              "/management/check_business_description",
              "GET",
              {
                function_l1: functionL1,
                function_l2: functionL2,
                management: management,
              },
              undefined,
              instance as PublicClientApplication
            );

            setBusinessDescriptionData(checkResponse);
          } catch (checkError) {
            console.error("Error checking business description:", checkError);
            setBusinessDescriptionData(null);
            setApiStatus("error");
          }
        }
      } finally {
        setIsCheckingData(false);
      }
    }
  };

  useEffect(() => {
    checkBusinessDescriptions();
  }, [functionL1, functionL2, management, isManagementMatching, refreshKey]);

  const handleSuccess = async () => {
    setRefreshKey((prev) => prev + 1); // Trigger re-fetch
  };

  const handleReload = async () => {
    checkBusinessDescriptions();
  };

  // Reset states when functionL1 changes
  useEffect(() => {
    if (functionL1) {
      setFunctionL2("");
      setBusinessDescriptionData(null);
      setFilteredBDData(null);
      setApiStatus("initial");
    }
  }, [functionL1]);

  // Create Description Handler
  const handleCreateDescription = () => {
    setShowSIPOC(true);
  };


  // Button Disabled State
  const isCreateButtonDisabled =
    !functionL1 ||
    !functionL2 ||
    isCheckingData ||
    !isManagementMatching ||
    (businessDescriptionData && businessDescriptionData.data !== null);

  // Breadcrumbs Data
  const breadcrumbs = [
    <Link key="1" color="#000" to="/">
      Home
    </Link>,
    <Link key="2" color="#000" to="/operations/management">
      Evidence Upload
    </Link>,
    <Link key="3" color="#000" to="/operations/management">
      Management
    </Link>,
    <Typography key="4" sx={{ fontWeight: "bold" }}>
      {management}
    </Typography>,
  ];

  const renderContent = () => {
    if (isCheckingData) {
      return (
        <Typography
          variant="body1"
          gutterBottom
          className={Styles.createNewPara}
        >
          Checking for existing business descriptions...
        </Typography>
      );
    }

    if (apiStatus === "error") {
      return (
        <Typography
          variant="body1"
          gutterBottom
          className={Styles.createNewPara}
        >
          No record found for selected L1 & L2.
        </Typography>
      );
    }

    // Always show filtered data if available, regardless of management
    if (filteredBDData) {
      return (
        <Box sx={{ minWidth: "100%" }}>
          <CheckBDStatus
            functionL1={functionL1}
            functionL2={functionL2}
            management={management}
            businessDescriptionData={filteredBDData}
            onSuccess={handleSuccess}
            onReload={handleReload}
          />
          {/* Only show create button if management matches */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            {isManagementMatching && !businessDescriptionData?.data && (
              <div className={Styles.TextBoxbussiness}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateDescription}
                  disabled={isCreateButtonDisabled}
                  sx={{
                    mt: 2,
                    opacity: isCreateButtonDisabled ? 0.5 : 1,
                    textTransform: "capitalize",
                  }}
                >
                  Create New Business Description
                </Button>
              </div>
            )}

            <Button
              onClick={() => setOpenUpload(true)}
              variant="outlined"
              disabled={!functionL1 || !functionL2 || isCheckingData}
              sx={{
                color: "#000",
                mt: 2,
                textTransform: "capitalize",
                ml: 2,
                opacity: !functionL1 || !functionL2 || isCheckingData ? 0.5 : 1,
              }}
            >
              Upload Exsiting File
            </Button>
          </Box>
        </Box>
      );
    }

    // Show management-specific data if available
    if (businessDescriptionData?.data) {
      return (
        <Box sx={{ minWidth: "100%" }}>
          <CheckBDStatus
            functionL1={functionL1}
            functionL2={functionL2}
            management={management}
            businessDescriptionData={businessDescriptionData}
            onReload={handleReload}
          />
        </Box>
      );
    }

    // No data found at all
    return (
      <div className={Styles.TextBoxbussiness}>
        <Typography
          variant="body1"
          gutterBottom
          className={Styles.createNewPara}
        >
          No business description found for the selected functions.
        </Typography>
        {/* Only show create button if management matches */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          {isManagementMatching && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateDescription}
              disabled={isCreateButtonDisabled}
              sx={{
                mt: 2,
                opacity: isCreateButtonDisabled ? 0.5 : 1,
                textTransform: "capitalize",
              }}
            >
              Create New Business Description
            </Button>
          )}

          <Button
            onClick={() => setOpenUpload(true)}
            variant="outlined"
            disabled={!functionL1 || !functionL2 || isCheckingData}
            sx={{
              color: "#000",
              mt: 2,
              textTransform: "capitalize",
              ml: 2,
              opacity: !functionL1 || !functionL2 || isCheckingData ? 0.5 : 1,
            }}
          >
            Upload Exsiting File
          </Button>
        </Box>
      </div>
    );
  };

  return (
    <>
      <ActivityBar breadcrumbs={breadcrumbs} />
      <div className={Styles.mainContent}>

        <SectionSlider
          sections={sections}
          sectionPaths={sectionPaths}
          highlightSection={"Business Description"}
          highlightColor="#D7A833"
          defaultColor="#666"
        />

        {!showSIPOC ? (
          <div className={Styles.selectFunc}>
            <Box className={Styles.filterMainBox}>
              <Box sx={{ display: "flex", gap: "30px" }}>
                <div className={Styles.selectMain}>
                  <label>Select Function L1</label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={functionL1}
                      onChange={(e) => setFunctionL1(e.target.value)}
                      disabled={isCheckingData || l1Loading}
                    >
                      <option value="" disabled>
                        Select Function L1
                      </option>
                      {l1Options.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {l1Loading && (
                      <CircularProgress
                        size={20}
                        style={{
                          position: "absolute",
                          right: "20px",
                          top: "35%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className={Styles.selectMain}>
                  <label>Select Function L2</label>
                  <div style={{ position: "relative" }}>
                    <select
                      value={functionL2}
                      onChange={(e) => setFunctionL2(e.target.value)}
                      disabled={!functionL1 || isCheckingData || l2Loading}
                    >
                      <option value="" disabled>
                        {functionL1
                          ? l2Loading
                            ? "Loading options..."
                            : "Select Function L2"
                          : "Select Function L1 first"}
                      </option>
                      {l2Options.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {l2Loading && (
                      <CircularProgress
                        size={20}
                        style={{
                          position: "absolute",
                          right: "20px",
                          top: "35%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    )}
                  </div>
                </div>
              </Box>
            </Box>

            <Paper
              elevation={0}
              className={Styles.createNewBox}
              sx={{ p: 0, textAlign: "center", height: "100%" }}
            >
              {renderContent()}
            </Paper>
          </div>
        ) : (
          <BusinessDescriptionForm
            functionL1={functionL1}
            functionL2={functionL2}
            management={management}
          />
        )}
      </div>

      <UploadExsitingBdPopup
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        management={management}
        functionL1={functionL1}
        functionL2={functionL2}
        onSuccess={handleSuccess}
      />

    </>
  );
};

export default BusinessDescription;
